import React, { useEffect } from 'react';
import { useParams, Navigate, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/axios';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { DynamicForm } from '../../components/common/DynamicForm';

export const CourseDetail: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { data: course, isLoading, isError } = useQuery({
        queryKey: ['publicCourse', slug],
        queryFn: async () => {
            const response = await apiClient.get(`/public/courses/${slug}`);
            return response.data.data;
        },
        enabled: !!slug,
        retry: false
    });

    useEffect(() => {
        if (course?.title) {
            document.title = `${course.title} | Mosaik Offenbach`;
        }
    }, [course?.title]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-32 min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (isError || !course) {
        return <Navigate to="/kursangebot" replace />;
    }

    const resolvedImageUrl = resolveAssetUrl(course.imageUrl);
    
    return (
        <div className="flex flex-col bg-background min-h-screen pb-16">
            <div
                className="h-64 sm:h-80 relative flex items-center justify-center text-center px-4"
                style={{
                    background: resolvedImageUrl
                        ? `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${resolvedImageUrl}) center/cover no-repeat`
                        : (course.color || 'var(--color-primary)')
                }}
            >
                <div>
                    <h1 className="text-3xl sm:text-5xl font-bold text-white mb-4">{course.title}</h1>
                </div>
            </div>

            <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 w-full -mt-8 relative z-10">
                <nav className="mb-6 text-sm font-medium text-white/80 flex items-center gap-2 bg-foreground/50 w-max px-4 py-2 rounded-full backdrop-blur-md">
                    <Link to="/" className="hover:text-white transition-colors">{t('common.home')}</Link>
                    <span>/</span>
                    <Link to="/kursangebot" className="hover:text-white transition-colors">{t('courses.breadcrumb') || 'Kursangebot'}</Link>
                    <span>/</span>
                    <span className="text-white">{course.title}</span>
                </nav>

                <div className="bg-white rounded-3xl shadow-sm border border-border p-8 sm:p-12 text-lg leading-relaxed text-foreground/80 whitespace-pre-line">
                    <button
                        onClick={() => navigate('/kursangebot')}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors mb-8"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {t('events.back_to_overview')}
                    </button>

                    {/* Horizontal Info Bar Container */}
                    {((course.registrationUrl && course.registrationUrl.trim() !== '') || !course.formDefinitionId) && (
                        <div className="bg-foreground/[0.02] border border-border rounded-3xl p-6 mb-12">
                            <div className="flex flex-col sm:flex-row gap-4">
                                {course.registrationUrl && course.registrationUrl.trim() !== '' ? (
                                    <a 
                                        href={course.registrationUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                                    >
                                        <FileText className="w-5 h-5" />
                                        {t('courses.register_now', 'Jetzt anmelden')}
                                    </a>
                                ) : (
                                    <button 
                                        onClick={() => navigate('/kontakt')}
                                        className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                                    >
                                        <FileText className="w-5 h-5" />
                                        {t('courses.register_now', 'Jetzt anmelden')}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Main Description */}
                    <div>
                        <h2 className="text-2xl font-bold mb-6 text-foreground border-b border-border pb-4">
                            {t('common.description') || 'Beschreibung'}
                        </h2>
                        <div className="prose prose-lg max-w-none text-foreground/80 leading-relaxed mb-12">
                            {course.description}
                        </div>

                        {course.formDefinitionId && (
                            <div className="mt-16 pt-16 border-t border-border">
                                <DynamicForm 
                                    formId={course.formDefinitionId} 
                                    relatedEntityId={course.id}
                                    relatedEntityType="Course"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
